import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IDynamicLottieChange } from 'lottie-json-helper/lib/types';
import { BehaviorSubject, Subject, throttleTime } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IComposedEditTakeConfigs } from 'src/app/models/job/editor-defines';
import { IDynamicLottieData } from 'src/app/models/lottie/lottie-defines';
import { IFakeDetection } from 'src/app/models/defines';
import { buildEditorRows, EditorRow } from './dynamic-row';

// Backwards-compat re-exports: the row classes + helpers used to live in this
// file. External callers that imported them by name still work after the
// extract — keep the surface area identical.
export {
    BaseRow,
    buildEditorRows,
    buildSingleRow,
    classifyInputKind,
    ColorRow,
    deriveRange,
    deriveTripletLabel,
    displayLabel,
    EditorRow,
    formatNumberForDisplay,
    formatTextForDisplay,
    hexToRgba01,
    humanize,
    InputKind,
    NumberRange,
    NumberRow,
    normalizeHex6,
    RgbRow,
    rgba01ArrayToHex,
    rgbTripletToHex,
    TextRow,
    ToggleRow,
    toNumber,
    tryFindRgbTriplet,
} from './dynamic-row';

/**
 * Optional payload that lets the editor render a live scene preview alongside
 * the value table. Everything here is treated as read-only source material —
 * the editor clones it and only ever mutates its own copy, so nothing leaks
 * back to the take until the caller acts on the Save result.
 */
export interface IDynamicLottieEditorPreview {
    /** Composed scenes render <app-lottie-video-composed>; others <dynamic-lottie>. */
    toDisplayComposed: boolean;
    /** Full compose configs for the composed player (video + lottie layers). */
    composeConfigs?: IComposedEditTakeConfigs;
    /** Lottie layout for the non-composed <dynamic-lottie> path. */
    layout?: IDynamicLottieData['layout'];
    /**
     * Scene aspect ratio (e.g. '16/9'). The players fill their box but have no
     * intrinsic height, so the preview stage needs an explicit ratio or it
     * collapses to zero height. Value is a valid CSS `aspect-ratio`.
     */
    aspectRatio?: string;
}

export interface IDynamicLottieEditorData {
    changes: IDynamicLottieChange[];
    /**
     * Optional absolute URL of the source Lottie JSON. When present, the
     * editor shows a small copy-link button next to its title (URL revealed
     * on hover) so the same file can be opened in /lottie for live debugging.
     */
    jsonUrl?: string;
    /**
     * Optional live-preview source. When present the dialog shows a scene
     * preview that re-renders as values change — WITHOUT touching the take.
     */
    preview?: IDynamicLottieEditorPreview;
}

@Component({
    selector: 'app-dynamic-lottie-editor',
    templateUrl: './dynamic-lottie-editor.component.html',
    styleUrls: ['./dynamic-lottie-editor.component.scss'],
})
export class DynamicLottieEditorComponent implements OnInit, OnDestroy {
    /** Authored once at construction; bound to plain row properties in template. */
    readonly rows: ReadonlyArray<EditorRow>;
    readonly jsonUrl: string | null;
    /** Brief "Copied!" hint on the copy button after a successful clipboard write. */
    justCopied = false;

    /** Whether a live preview pane should be shown at all. */
    readonly hasPreview: boolean;
    readonly toDisplayComposed: boolean;
    /** CSS aspect-ratio for the preview stage so the player has a height. */
    readonly previewAspectRatio: string;
    /** New reference on every value change so the players re-render. */
    previewComposeConfigs: IComposedEditTakeConfigs | null = null;
    previewLottieConfig: IDynamicLottieData | null = null;
    /** Autoplay signal for the composed player. */
    readonly playOrPause$ = new BehaviorSubject<IFakeDetection>({ isPlaying: true, randomNumber: 1 });

    /**
     * The editor's OWN copy of the changes. Rows mutate these in place; the take
     * is never touched until the caller consumes the Save result. buildEditorRows
     * binds each row to one of these objects.
     */
    private readonly workingChanges: IDynamicLottieChange[];
    /** Deep clone of the incoming compose configs — mutated only on our copy. */
    private readonly previewBaseComposeConfigs: IComposedEditTakeConfigs | null;
    private readonly previewLayout: IDynamicLottieData['layout'] | null;
    /** Coalesces rapid edits (slider drags) into at most one re-render per window. */
    private readonly refresh$ = new Subject<void>();
    private readonly onDestroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<DynamicLottieEditorComponent>,
        @Inject(MAT_DIALOG_DATA) data: IDynamicLottieEditorData
    ) {
        // Isolate: rows reassign `change.value` wholesale, so a shallow per-change
        // clone is enough to keep every edit off the caller's original objects.
        this.workingChanges = (data.changes ?? []).map((c) => ({ ...c }));
        this.rows = buildEditorRows(this.workingChanges);
        this.jsonUrl = data.jsonUrl?.trim() || null;

        const preview = data.preview;
        this.hasPreview = !!preview;
        this.toDisplayComposed = !!preview?.toDisplayComposed;
        this.previewBaseComposeConfigs = preview?.composeConfigs ? this.deepClone(preview.composeConfigs) : null;
        this.previewLayout = preview?.layout ?? null;
        this.previewAspectRatio = preview?.aspectRatio || this.previewBaseComposeConfigs?.aspectRatio || '16/9';
    }

    ngOnInit(): void {
        this.refresh$
            .pipe(throttleTime(200, undefined, { leading: true, trailing: true }), takeUntil(this.onDestroy$))
            .subscribe(() => {
                this.rebuildPreview();
            });
        this.rebuildPreview();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    /** Called from every <app-dynamic-value-control> (valueChange). */
    onValueChanged(): void {
        if (!this.hasPreview) return;
        this.refresh$.next();
    }

    /**
     * Rebuild the preview inputs from the current working changes. Composed
     * scenes mutate the cloned configs' first lottie layer (mirroring
     * scene-setup.preview()) and hand a fresh top-level reference to the player;
     * non-composed scenes get a fresh IDynamicLottieData.
     */
    private rebuildPreview(): void {
        if (!this.hasPreview) return;
        const changesSnapshot = this.workingChanges.map((c) => ({ ...c }));

        if (this.toDisplayComposed) {
            const base = this.previewBaseComposeConfigs;
            const lottieData = base?.videoLottieConfigs?.[0]?.lottieData;
            if (!base || !lottieData) {
                this.previewComposeConfigs = null;
                return;
            }
            lottieData.dynamicLottieChanges = changesSnapshot;
            this.previewComposeConfigs = { ...base };
        } else {
            this.previewLottieConfig = {
                layout: this.previewLayout,
                dynamicLottieChanges: changesSnapshot,
            };
        }
    }

    /** Structured clone with a JSON fallback for older runtimes. Configs are plain data. */
    private deepClone<T>(value: T): T {
        try {
            return structuredClone(value);
        } catch {
            return JSON.parse(JSON.stringify(value));
        }
    }

    onSave(): void {
        this.dialogRef.close(this.workingChanges);
    }

    onCancel(): void {
        this.dialogRef.close(null);
    }

    async copyJsonUrl(): Promise<void> {
        if (!this.jsonUrl) return;
        try {
            await navigator.clipboard.writeText(this.jsonUrl);
            this.justCopied = true;
            setTimeout(() => (this.justCopied = false), 1400);
        } catch {
            // Clipboard API can refuse in non-secure contexts; the tooltip
            // already exposes the URL so the user can copy it manually.
        }
    }

    /** Identity trackBy — rows are immutable wrappers, never reallocated. */
    trackRow(_index: number, row: EditorRow): EditorRow {
        return row;
    }
}
